<script>

    import { CARE_RECIPIENT_CONTACT_TYPE, util } from '@/Helpers'

    export const getContactEmail = (contact) => {
        const email = contact?.emails && contact.emails[0] ? String(contact.emails[0]).trim() : null
        return email && util.isValidEmail(email) ? email.toLowerCase() : null
    }

    const hasValidPrimaryEmail = (contact) => {
        return Boolean(getContactEmail(contact))
    }

    const uniqueContactsById = (contacts) => {
        const uniqueContacts = new Map()

        ;(contacts ?? []).forEach((contact) => {
            const id = contact?.id

            if (id == null) {
                return
            }

            const key = String(id)

            if (!uniqueContacts.has(key)) {
                uniqueContacts.set(key, contact)
            }
        })

        return Array.from(uniqueContacts.values())
    }

    export const getDefaultClientSignerSelection = (contacts = []) => {
        const signerContacts = uniqueContactsById((contacts ?? []).filter((contact) => {
            return hasValidPrimaryEmail(contact) && (contact?.primary_contact || contact?.billing_contact)
        }))

        if (signerContacts.length === 0) {
            return {
                primarySignerId: null,
                additionalSignerIds: [],
                hipaaSignerId: null,
            }
        }

        if (signerContacts.length === 1) {
            const primarySignerId = signerContacts[0].id

            return {
                primarySignerId,
                additionalSignerIds: [],
                hipaaSignerId: primarySignerId,
            }
        }

        const sortedSignerContacts = [...signerContacts].sort((a, b) => Number(a.id) - Number(b.id))
        const clientPrimary = sortedSignerContacts.find((contact) => contact.type === CARE_RECIPIENT_CONTACT_TYPE)

        if (clientPrimary) {
            return {
                primarySignerId: clientPrimary.id,
                additionalSignerIds: sortedSignerContacts
                    .filter((contact) => contact.id !== clientPrimary.id)
                    .slice(0, 3)
                    .map((contact) => contact.id),
                hipaaSignerId: clientPrimary.id,
            }
        }

        return {
            primarySignerId: sortedSignerContacts[0].id,
            additionalSignerIds: sortedSignerContacts.slice(1, 4).map((contact) => contact.id),
            hipaaSignerId: sortedSignerContacts[0].id,
        }
    }

</script>

<script setup>

    import { computed, ref, watch, nextTick, reactive, useTemplateRef } from 'vue'
    import { storeToRefs } from 'pinia'
    import { IconQuestionCircle12Filled } from '@iconify-prerendered/vue-fluent'

    import MultiSelect from 'primevue/multiselect'
    import Select from 'primevue/select'
    import InlineModal from '@/Components/App/InlineModal.vue'
    import LatteUpdateContactEmailDialog from '@/Components/Client/LatteUpdateContactEmailDialog.vue'
    import { util, CARE_RECIPIENT_CONTACT_TYPE, listToSelectOptions } from '@/Helpers'
    import { SERVICE_VARIANT_OPTIONS, useClientSingleStore, useConfigStore, useStaffStore, useNotificationStore } from '@/Stores'
    import TextInput from '@/Components/Forms/TextInput.vue'
    import TextInputWithUnit from '@/Components/Forms/TextInputWithUnit.vue'

    const props = defineProps({
        show: {
            type: Boolean,
            default: false,
        },
        submitting: {
            type: Boolean,
            default: false,
        },
        agencyOptions: {
            type: Array,
            default: () => [],
        },
        documentOptions: {
            type: Array,
            default: () => [],
        },
        staffOptions: {
            type: Array,
            default: () => [],
        },
        initialSelectedDocumentIds: {
            type: Array,
            default: () => [],
        }
    })

    const emit = defineEmits(['close', 'submit', 'start-tour-at-step'])

    const configStore = useConfigStore()
    const staffStore = useStaffStore()
    const clientSingleStore = useClientSingleStore()

    const { config } = storeToRefs(configStore)
    const { activeStaffs } = storeToRefs(staffStore)
    const { client } = storeToRefs(clientSingleStore)

    const SERVICE_CLASS_OPTIONS = [
        {
            id: 'Companionship',
            name: 'Companionship',
        },
        {
            id: 'Home Health Care',
            name: 'Home Health Care',
        }
    ]

    const companyDisplayNames = {
        john_hancock: 'John Hancock',
        brighthouse: 'Brighthouse',
        genworth: 'Genworth',
    }

    const ltciCompanyOptions = computed(() => {
        const companies = config.value.documents?.insurance_companies ?? []
        return listToSelectOptions(companies, null, (item) => companyDisplayNames[item] || item)
    })

    const facilityAgencyOptions = [
        {
            id: 'lifeworx-inc-ny',
            name: 'Lifeworx, Inc. (864 Lexington Ave, NY)',
        },
        {
            id: 'lifeworx-inc-nj',
            name: 'Lifeworx, Inc. (46 N Dean St, NJ)',
        },
        {
            id: 'lifeworx-care-llc',
            name: 'Lifeworx Care, LLC (864 Lexington Ave, NY)',
        },
        {
            id: 'lifeworx-florida-llc',
            name: 'Lifeworx Florida, LLC (1425 Main St, FL)',
        },
        {
            id: 'lifeworx-south-carolina-llc',
            name: 'Lifeworx South Carolina, LLC (864 Lexington Ave, NY)',
        }
    ]

    const selectedPrimarySigner = ref(null)
    const selectedCareForClientContacts = ref([])
    const selectedClientAgreementSigners = ref([])
    const selectedAgency = ref(null)
    const selectedAgencySigner = ref(null)
    const selectedHipaaSigner = ref(null)
    const selectedDocuments = ref([])

    const serviceAgreementSelected = ref({
        nurseAgreementRate: null,
        serviceClass: 'Companionship',
        hourlyRate: null,
        dailyRate: null,
        variant: 'general',
        hhaFrequency: null,
        rnAssessmentFrequency: '6 months',
    })

    const placementAgreementSelected = ref({
        registrationFee: 400,
        permanentPlacementFee: 20,
    })

    const ltciAssignmentOfBenefitsSelected = ref({
        insurance_company_name: null,
        insurance_policy_number: null,
        insurance_claim_number: null,
        facility_agency: null,
    })

    const healthcarePatientBillOfRightsSelected = ref({
        state: null,
    })

    const healthcareProxyUploadSelected = ref({
        uploader: null,
    })

    const powerOfAttorneyUploadSelected = ref({
        uploader: null,
    })

    const ltciAuthorizationSelected = ref({
        policyNumber: null,
        ssn: null,
        insurance_company_name: null,
    })

    // Template refs for tour targeting
    const agencySectionRef = useTemplateRef('tour-agency-section')
    const careRecipientsSectionRef = useTemplateRef('tour-care-recipients-section')
    const clientSignersSectionRef = useTemplateRef('tour-client-signers-section')
    const primarySignerSectionRef = useTemplateRef('tour-primary-signer-section')
    const additionalSignersSectionRef = useTemplateRef('tour-additional-signers-section')
    const hipaaSignerSectionRef = useTemplateRef('tour-hipaa-signer-section')
    const templatesSectionRef = useTemplateRef('tour-templates-section')

    // Email update dialog state
    const showEmailDialog = ref(false)
    const contactToUpdate = ref(null)
    const emailDialogLoading = ref(false)
    const notificationStore = useNotificationStore()

    // When we programmatically set contact selections we want to avoid
    // triggering the selection watcher that prompts to update missing emails.
    const skipContactSelectionWatcher = ref(false)

    // Keys used to force remount of Select components so their internal
    // state can't re-sync a stale value back into the v-model.
    const selectKeys = reactive({ primary: 0, hipaa: 0, uploader: 0, signers: 0 })

    function formatCurrencyId(input) {
        return Number(input).toFixed(2)
    }

    function formatCurrencyName(input) {
        return util.currency(input)
    }

    function createCurrencySelectOptions(list) {
        return (list ?? []).map((item, index) => ({
            id: formatCurrencyId(item, index),
            name: formatCurrencyName(item, index),
        }))
    }

    const normalizeOptions = (records) => {
        return (records ?? []).map((record, index) => {
            let name = [record.first_name, record.last_name].filter(Boolean).join(' ').trim() || record.name || 'Unknown'
            name += record.type ? ` (${record.type })` : ''
            return {
                id: record.id ?? record.email ?? index,
                name: name,
                raw: record,
            }
        })
    }

    const normalizeDocumentGroups = (records) => {
        const sourceGroups = records ?? []

        if (sourceGroups.length === 0) {
            return []
        }

        if (Array.isArray(sourceGroups[0]?.documents) || Array.isArray(sourceGroups[0]?.items)) {
            return sourceGroups.map((group, index) => ({
                id: group.id ?? group.key ?? index,
                documents: group.documents ?? group.items ?? [],
            }))
        }

        return [
            {
                id: 'default',
                documents: sourceGroups,
            },
        ]
    }

    const agencyOptions = computed(() => normalizeOptions(props.agencyOptions.length > 0 ? props.agencyOptions : (config.value.agency ?? [])))
    const documentGroups = computed(() => normalizeDocumentGroups(props.documentOptions.length > 0 ? props.documentOptions : (config.value.documents.groups ?? [])))
    const documentOptions = computed(() => documentGroups.value.flatMap((group) => group.documents))
    const staffOptions = computed(() => normalizeOptions(props.staffOptions.length > 0 ? props.staffOptions : (activeStaffs.value ?? [])))
    const clientContactOptions = computed(() => normalizeOptions(client.value?.contacts ?? []))

    // Filtered option lists to enforce mutual exclusion between primary and additional signers
    const primaryContactOptions = computed(() => {
        const exclude = new Set(selectedClientAgreementSigners.value || [])
        return clientContactOptions.value.filter(o => !exclude.has(o.id))
    })

    const signerContactOptions = computed(() => {
        const primary = selectedPrimarySigner.value
        return clientContactOptions.value.filter(o => o.id !== primary)
    })
    const hourlyRateOptions = computed(() => createCurrencySelectOptions(config.value.prospect_rates?.hourly ?? []))
    const dailyRateOptions = computed(() => createCurrencySelectOptions(config.value.prospect_rates?.daily ?? []))

    const healthcareBillOfRightsStateOptions = computed(() => {
        const allowedStates = ['FL', 'NJ', 'NY']
        return allowedStates.map(state => ({
            id: state,
            name: state,
        }))
    })

    // Track if service class was auto-set by agency selection
    const isServiceClassAutoSet = ref(false)

    // Detect when Home Health Care service class is selected
    const isHomeHealthCareSelected = computed(() =>
        serviceAgreementSelected.value.serviceClass === 'Home Health Care'
    )

    // Auto-populate healthcare bill of rights state from care for contact
    watch(selectedCareForClientContacts, (newContacts) => {
        let newState = null
        if (newContacts && newContacts.length > 0) {
            const contactId = newContacts[0]
            const contact = client.value?.contacts?.find(c => c.id === contactId)
            newState = contact?.state ? String(contact.state).toUpperCase() : null
        }
        healthcarePatientBillOfRightsSelected.value.state = newState
    }, { flush: 'post' })

    const getDefaultFacilityAgency = (agencyId, state) => {
        state = String(state ?? '').trim().toUpperCase()

        if (agencyId === 'lifeworx-inc') {
            return state === 'NJ' ? 'lifeworx-inc-nj' : 'lifeworx-inc-ny'
        } else if (agencyId === 'lifeworx-home-health-services-inc') {
            return 'lifeworx-care-llc'
        } else if (agencyId === 'lifeworx-florida-llc') {
            return 'lifeworx-florida-llc'
        } else if (agencyId === 'lifeworx-south-carolina-llc') {
            return 'lifeworx-south-carolina-llc'
        } else if (agencyId === 'lifeworx-inc-nj') {
            return 'lifeworx-inc-nj'
        }

        return null
    }

    const applyLtciDefaults = (contact) => {
        // LTCI fields only apply to client-type contacts
        if (!contact || contact.type !== CARE_RECIPIENT_CONTACT_TYPE) {
            // Reset all LTCI fields when no contact or not a client contact
            ltciAssignmentOfBenefitsSelected.value.insurance_company_name = null
            ltciAssignmentOfBenefitsSelected.value.insurance_policy_number = null
            ltciAssignmentOfBenefitsSelected.value.insurance_claim_number = null
            ltciAuthorizationSelected.value.insurance_company_name = null
            ltciAuthorizationSelected.value.policyNumber = null
            ltciAuthorizationSelected.value.ssn = null
            return
        }

        // Auto-assign LTCI insurance company from the main client if it has one and it's in the LTCI companies list
        const clientInsuranceCompany = client.value?.ltc_insurance_company
        if (clientInsuranceCompany && ltciCompanyOptions.value.some(opt => opt.id === clientInsuranceCompany)) {
            ltciAssignmentOfBenefitsSelected.value.insurance_company_name = clientInsuranceCompany
            ltciAssignmentOfBenefitsSelected.value.insurance_policy_number = client.value?.ltc_insurance_policy_number ?? null
            ltciAssignmentOfBenefitsSelected.value.insurance_claim_number = client.value?.ltc_insurance_claim_number ?? null
            ltciAuthorizationSelected.value.insurance_company_name = clientInsuranceCompany
            ltciAuthorizationSelected.value.policyNumber = client.value?.ltc_insurance_policy_number ?? client.value?.ltc_insurance_claim_number ?? null
        } else {
            ltciAssignmentOfBenefitsSelected.value.insurance_company_name = null
            ltciAssignmentOfBenefitsSelected.value.insurance_policy_number = null
            ltciAssignmentOfBenefitsSelected.value.insurance_claim_number = null
            ltciAuthorizationSelected.value.insurance_company_name = null
            ltciAuthorizationSelected.value.policyNumber = null
        }
        ltciAuthorizationSelected.value.ssn = null
    }

    const applyClientDefaults = (value) => {
        const clientValue = value ?? {}
        serviceAgreementSelected.value.hourlyRate = clientValue?.rate_quoted_hourly != null ? Number(clientValue.rate_quoted_hourly).toFixed(2) : null
        serviceAgreementSelected.value.dailyRate = clientValue?.rate_quoted_daily != null ? Number(clientValue.rate_quoted_daily).toFixed(2) : null
        serviceAgreementSelected.value.serviceClass = clientValue?.is_healthcare ? 'Home Health Care' : 'Companionship'
        serviceAgreementSelected.value.nurseAgreementRate = clientValue?.is_healthcare ? 0 : null
        serviceAgreementSelected.value.variant = String(clientValue?.address?.state ?? '').trim().toUpperCase() === 'CT' ? 'ct' : 'general'
        selectedAgencySigner.value = clientValue?.assigned_sales_manager?.id ?? clientValue?.assigned_sales_manager?.user_id ?? null
        // Default selected agency based on client address state and healthcare flag
        const state = String(clientValue?.address?.state ?? '').trim().toUpperCase()
        if (!state) {
            selectedAgency.value = null
        } else if (state === 'NY' && clientValue?.is_healthcare) {
            // NY with healthcare flag gets home health services
            selectedAgency.value = 'lifeworx-home-health-services-inc'
        } else if (['NY', 'NJ', 'CT'].includes(state)) {
            // NY (without healthcare), NJ, CT get standard lifeworx-inc
            selectedAgency.value = 'lifeworx-inc'
        } else if (state === 'FL') {
            selectedAgency.value = 'lifeworx-florida-llc'
        } else if (state === 'SC') {
            selectedAgency.value = 'lifeworx-south-carolina-llc'
        } else {
            selectedAgency.value = null
        }
        const defaultFacilityAgency = getDefaultFacilityAgency(selectedAgency.value, clientValue?.address?.state)
        ltciAssignmentOfBenefitsSelected.value.facility_agency = defaultFacilityAgency

        const clientContact = (clientValue?.contacts ?? []).find(c => c.type === CARE_RECIPIENT_CONTACT_TYPE)
        applyLtciDefaults(clientContact)
    }

    const applyContactDefaults = (clientValue) => {
        const value = clientValue ?? {}
        const contacts = value?.contacts ?? []

        // Care Recipient defaults to the contact marked as a client (0 or 1)
        // Care recipients are not email-dependent, so derive from the full contacts list
        const clientContact = (contacts ?? []).find(c => c.type === CARE_RECIPIENT_CONTACT_TYPE)

        // Avoid firing the email update watcher while we apply defaults
        skipContactSelectionWatcher.value = true
        try {
            selectedCareForClientContacts.value = clientContact ? [clientContact.id] : []

            const signerDefaults = getDefaultClientSignerSelection(contacts)

            if (!signerDefaults.primarySignerId) {
                selectedPrimarySigner.value = null
                selectedClientAgreementSigners.value = []
                selectedHipaaSigner.value = null
                return
            }

            selectedPrimarySigner.value = signerDefaults.primarySignerId
            selectedClientAgreementSigners.value = signerDefaults.additionalSignerIds
            selectedHipaaSigner.value = signerDefaults.hipaaSignerId
        } finally {
            // ensure watcher resumes on next tick
            nextTick(() => { skipContactSelectionWatcher.value = false })
        }
    }


    const selectedDocumentRecords = computed(() => documentOptions.value.filter((document) => selectedDocuments.value.includes(document.id)))
    const hasServiceAgreementSelected = computed(() => selectedDocumentRecords.value.some((document) => isDocumentSelected(document, 'service-agreement')))
    const hasPaymentAgreementSelected = computed(() => selectedDocumentRecords.value.some((document) => isDocumentSelected(document, 'payment-agreement')))
    const hasPlacementAgreementSelected = computed(() => selectedDocumentRecords.value.some((document) => isDocumentSelected(document, 'placement-agreement')))
    const requiresAgreementSigners = computed(() =>
        hasServiceAgreementSelected.value ||
        hasPaymentAgreementSelected.value ||
        hasPlacementAgreementSelected.value
    )
    const hasLtciAssignmentOfBenefitsSelected = computed(() => selectedDocumentRecords.value.some((document) => isDocumentSelected(document, 'ltci-assignment-of-benefits')))
    const hasLtciAuthorizationSelected = computed(() => selectedDocumentRecords.value.some((document) => isDocumentSelected(document, 'ltci-authorization')))
    const hasHealthcareHipaaReleaseSelected = computed(() => selectedDocumentRecords.value.some((document) => isDocumentSelected(document, 'healthcare-hipaa-release')))
    const hasHealthcarePatientBillOfRightsSelected = computed(() => selectedDocumentRecords.value.some((document) => isDocumentSelected(document, 'healthcare-patient-bill-of-rights')))
    const hasHealthcareProxyUploadSelected = computed(() => selectedDocumentRecords.value.some((document) => isDocumentSelected(document, 'healthcare-proxy-upload')))
    const hasPowerOfAttorneyUploadSelected = computed(() => selectedDocumentRecords.value.some((document) => isDocumentSelected(document, 'power-of-attorney-upload')))
    const hasAttorneyUploadRequireDocSelected = computed(() => selectedDocumentRecords.value.some((document) => {
        return REQUIRED_ATTORNEY_UPLOAD_DOC_IDS.some(req => isDocumentSelected(document, req))
    }))
    const hasSelectedRate = computed(() => serviceAgreementSelected.value.hourlyRate !== null || serviceAgreementSelected.value.dailyRate !== null)
    // Check for email issues among selected contacts/signers
    const selectedCombinedIds = computed(() => {
        const ids = [...(selectedClientAgreementSigners.value || []), selectedPrimarySigner.value]
        // dedupe ids
        return Array.from(new Set(ids))
    })

    const emailSelectionIssues = computed(() => {
        const issues = {
            duplicates: [], // [{ email, contacts: [contact,...] }]
            invalids: [], // [contact,...]
        }

        const contacts = (selectedCombinedIds.value || []).map(id => {
            const option = clientContactOptions.value.find(o => o.id === id)
            return option?.raw ?? client.value?.contacts?.find(c => c.id === id)
        }).filter(Boolean)

        const emailMap = {}
        for (const contact of contacts) {
            const email = contact?.emails && contact.emails[0] ? String(contact.emails[0]).trim() : null
            if (!email || !util.isValidEmail(email)) {
                issues.invalids.push(contact)
                continue
            }
            const key = email.toLowerCase()
            if (!emailMap[key]) emailMap[key] = { email, contacts: [] }
            emailMap[key].contacts.push(contact)
        }

        for (const entry of Object.values(emailMap)) {
            if (entry.contacts.length > 1) {
                issues.duplicates.push({ email: entry.email, contacts: entry.contacts })
            }
        }

        return issues
    })




    const footerWarningText = computed(() => {
        if (selectedAgency.value == null) {
            return 'Select an agency'
        }

        // Only check email issues when signer fields are enabled
        if (requiresAgreementSigners.value) {
            const issues = emailSelectionIssues.value
            if (issues.duplicates && issues.duplicates.length > 0) {
                // build message listing first conflicting email and contact names (up to 3)
                const dup = issues.duplicates[0]
                const names = dup.contacts.map(c => ([c.first_name, c.last_name].filter(Boolean).join(' ').trim() || c.name || `Contact ${c.id}`))
                const sample = names.slice(0, 3).join(', ')
                return `Contacts ${sample} share the same email (${dup.email}). Update or choose different contacts.`
            }

            if (issues.invalids && issues.invalids.length > 0) {
                const names = issues.invalids.map(c => ([c.first_name, c.last_name].filter(Boolean).join(' ').trim() || c.name || `Contact ${c.id}`))
                const sample = names.slice(0, 3).join(', ')
                return `Contact(s) ${sample} do not have a valid primary email. Update the contact record.`
            }
        }

        if (selectedDocuments.value.length === 0) {
            return 'Select at least one document'
        }

        if (hasServiceAgreementSelected.value && hasSelectedRate.value === false) {
            return 'Select at least one rate'
        }

        if (hasServiceAgreementSelected.value && !serviceAgreementSelected.value.serviceClass) {
            return 'Select a service class'
        }

        // Check home health care required fields
        if (hasServiceAgreementSelected.value && isHomeHealthCareSelected.value) {
            if (!serviceAgreementSelected.value.hhaFrequency) {
                return 'Enter HHA Frequency'
            }
            if (!serviceAgreementSelected.value.rnAssessmentFrequency) {
                return 'Enter RN Assessment Frequency'
            }
        }

        // Ensure nurse assessment rate is not negative
        if (hasServiceAgreementSelected.value && serviceAgreementSelected.value.nurseAgreementRate != null) {
            if (Number(serviceAgreementSelected.value.nurseAgreementRate) < 0) {
                return 'Nurse Assessment Rate cannot be negative'
            }
        }

        // Check home health care required fields
        if (hasServiceAgreementSelected.value && isHomeHealthCareSelected.value) {
            if (!serviceAgreementSelected.value.hhaFrequency) {
                return 'Enter HHA Frequency'
            }
            if (!serviceAgreementSelected.value.rnAssessmentFrequency) {
                return 'Enter RN Assessment Frequency'
            }
        }

        // Ensure nurse assessment rate is not negative
        if (hasServiceAgreementSelected.value && serviceAgreementSelected.value.nurseAgreementRate != null) {
            if (Number(serviceAgreementSelected.value.nurseAgreementRate) < 0) {
                return 'Nurse Assessment Rate cannot be negative'
            }
        }

        if (
            hasLtciAuthorizationSelected.value &&
            !selectedCareForClientContacts.value?.length
        ) {
            return 'Select Care Recipient contact for the LTCI Authorization'
        }

        if (
            hasLtciAssignmentOfBenefitsSelected.value &&
            !selectedCareForClientContacts.value?.length
        ) {
            return 'Select Care Recipient contact for the LTCI Assignment of Benefits'
        }

        if (
            hasHealthcareHipaaReleaseSelected.value &&
            !selectedCareForClientContacts.value?.length
        ) {
            return 'Select Care Recipient contact for the Healthcare HIPAA Release'
        }

        if (
            (
                hasLtciAuthorizationSelected.value ||
                hasHealthcareHipaaReleaseSelected.value ||
                hasLtciAssignmentOfBenefitsSelected.value
            )
            && selectedCareForClientContacts.value?.length > 1
        ) {
            return 'Only one Care Recipient contact can be selected for HIPAA/LTCI documents'
        }

        if (
            (
                hasLtciAuthorizationSelected.value ||
                hasHealthcareHipaaReleaseSelected.value ||
                hasLtciAssignmentOfBenefitsSelected.value ||
                hasHealthcarePatientBillOfRightsSelected.value
            )
            && !selectedHipaaSigner.value
        ) {
            return 'Select a HIPAA/LTCI signer'
        }

        if (hasHealthcarePatientBillOfRightsSelected.value && !healthcarePatientBillOfRightsSelected.value.state) {
            return 'Select the state for the Healthcare Patient Bill of Rights document'
        }

        if (
            hasHealthcareProxyUploadSelected.value &&
            !healthcareProxyUploadSelected.value.uploader
        ) {
            return 'Select the Healthcare Proxy Uploader'
        }

        if (
            hasPowerOfAttorneyUploadSelected.value &&
            !powerOfAttorneyUploadSelected.value.uploader
        ) {
            return 'Select the Power of Attorney Uploader'
        }

        if (
            (
                hasServiceAgreementSelected.value ||
                hasPaymentAgreementSelected.value ||
                hasPlacementAgreementSelected.value
            ) &&
            !selectedPrimarySigner.value
        ) {
            return 'Select a primary signer'
        }

        if (hasServiceAgreementSelected.value && !selectedCareForClientContacts.value?.length) {
            return 'Select at least one Care Recipient contact for the Service Agreement'
        }

        if (hasLtciAssignmentOfBenefitsSelected.value && !ltciAssignmentOfBenefitsSelected.value.insurance_company_name) {
            return 'Select the LTCI company for the Assignment of Benefits document'
        }

        if (hasLtciAuthorizationSelected.value && hasLtciAssignmentOfBenefitsSelected.value && ltciAssignmentOfBenefitsSelected.value.insurance_company_name != ltciAuthorizationSelected.value.insurance_company_name) {
            return 'LTCI Authorization and Assignment of Benefits must use the same insurance'
        }

        return ''
    })

    // Derive send-ability from the footer warning text to keep them in sync.
    const canSendForSigning = computed(() => {
        return footerWarningText.value === ''
    })

    const selectedCareForContact = computed(() => {
        const contactId = selectedCareForClientContacts.value?.[0]
        if (!contactId) return null
        return client.value?.contacts?.find(c => c.id === contactId)
    })

    const careRecipientPrePopulationMessage = computed(() => {
        const contact = selectedCareForContact.value
        const firstName = contact?.first_name || 'The'
        const lastName = contact?.last_name || ''
        return `${firstName} ${lastName} care recipient's address and contact information is used to pre-populate this form`
    })

    // When the selected care for contact changes, update facility agency, insurance settings, and healthcare state
    watch(selectedCareForContact, (contact) => {
        // Update state for healthcare bill of rights
        let newState = null
        if (contact) {
            newState = contact.state ? String(contact.state).toUpperCase() : null
        }
        healthcarePatientBillOfRightsSelected.value.state = newState

        const defaultFacilityAgency = getDefaultFacilityAgency(selectedAgency.value, contact?.state)
        ltciAssignmentOfBenefitsSelected.value.facility_agency = defaultFacilityAgency

        // Update facility agency and insurance info based on whether contact is a client
        if (contact && contact.type === CARE_RECIPIENT_CONTACT_TYPE) {
            applyLtciDefaults(contact)
        } else {
            applyLtciDefaults(null)
        }
    }, { flush: 'post' })

    const closeDialog = () => {
        emit('close')
    }

    const resetDialog = () => {
        selectedCareForClientContacts.value = []
        selectedClientAgreementSigners.value = []
        selectedPrimarySigner.value = null
        selectedAgency.value = null
        // Reapply client-driven defaults for agency signer and rates
        applyClientDefaults(client.value)
        selectedHipaaSigner.value = null
        selectedDocuments.value = []
        const isHomeHealthCare = client.value?.is_healthcare
        serviceAgreementSelected.value = {
            serviceClass: isHomeHealthCare ? 'Home Health Care' : 'Companionship',
            hourlyRate: null,
            dailyRate: null,
            variant: String(client.value?.address?.state ?? '').trim().toUpperCase() === 'CT' ? 'ct' : 'general',
            nurseAgreementRate: isHomeHealthCare ? 0 : null,
            hhaFrequency: null,
            rnAssessmentFrequency: '6 months',
        }
        placementAgreementSelected.value = {
            registrationFee: 400,
            permanentPlacementFee: 20,
        }
        ltciAuthorizationSelected.value = {
            policyNumber: null,
            ssn: null,
            insurance_company_name: null,
        }
        ltciAssignmentOfBenefitsSelected.value = {
            insurance_company_name: null,
            insurance_policy_number: null,
            insurance_claim_number: null,
            facility_agency: null,
        }
        healthcarePatientBillOfRightsSelected.value = {
            state: null,
        }
        showEmailDialog.value = false
        contactToUpdate.value = null
        emailDialogLoading.value = false
    }

    // Reset dialog state when it's closed (e.g., after a successful submit)
    watch(() => props.show, (show) => {
        if (!show) {
            resetDialog()
        } else {
            applyClientDefaults(client.value)
            applyContactDefaults(client.value)
            selectedDocuments.value = props.initialSelectedDocumentIds || []
            // prune any selections that reference soft-deleted contacts
            pruneDeletedContactSelections()
            ensureHealthcareProxySelected()
        }
    })

    // Remove any references to contacts that have been soft-deleted
    function pruneDeletedContactSelections() {
        try {
            const contacts = client.value?.contacts ?? []
            const validIds = new Set((contacts ?? []).filter(c => !c?.deleted_at).map(c => String(c.id)))

            // Primary signer
            if (selectedPrimarySigner.value && !validIds.has(String(selectedPrimarySigner.value))) {
                selectedPrimarySigner.value = null
                selectKeys.primary++
            }

            // Additional signers (array)
            const before = Array.isArray(selectedClientAgreementSigners.value) ? [...selectedClientAgreementSigners.value] : []
            selectedClientAgreementSigners.value = (before || []).filter(id => validIds.has(String(id)))
            if ((before || []).length !== selectedClientAgreementSigners.value.length) {
                selectKeys.signers++
            }

            // HIPAA/LTCI signer
            if (selectedHipaaSigner.value && !validIds.has(String(selectedHipaaSigner.value))) {
                selectedHipaaSigner.value = null
                selectKeys.hipaa++
            }

        // Healthcare proxy uploader
        if (healthcareProxyUploadSelected.value?.uploader && !validIds.has(String(healthcareProxyUploadSelected.value.uploader))) {
            healthcareProxyUploadSelected.value.uploader = null
            selectKeys.uploader++
        }

        // Power of attorney uploader
        if (powerOfAttorneyUploadSelected.value?.uploader && !validIds.has(String(powerOfAttorneyUploadSelected.value.uploader))) {
            powerOfAttorneyUploadSelected.value.uploader = null
            selectKeys.uploader++
        }
    } catch (e) {
        // best-effort only
    }
}

// Watch for changes to the client's contacts (soft-deletes) and prune selections when needed
watch(() => client.value?.contacts, () => {
        // only act when dialog is open to avoid surprising mutations
        if (!props.show) return
        pruneDeletedContactSelections()
    }, { deep: true })

    // Watch for agency changes and auto-set service class when home health agency is selected
    watch(() => selectedAgency.value, (newAgency) => {
        if (newAgency === 'lifeworx-home-health-services-inc') {
            serviceAgreementSelected.value.serviceClass = 'Home Health Care'
            isServiceClassAutoSet.value = true
        } else {
            isServiceClassAutoSet.value = false
        }
    })

    // Watch for service class changes and update nurse assessment rate default if not manually edited
    watch(() => serviceAgreementSelected.value.serviceClass, (newClass, oldClass) => {
        const currentRate = serviceAgreementSelected.value.nurseAgreementRate
        const oldDefault = oldClass === 'Home Health Care' ? 0 : null

        // Only update if current value matches the old default (user hasn't manually changed it)
        if (currentRate === oldDefault) {
            serviceAgreementSelected.value.nurseAgreementRate = newClass === 'Home Health Care' ? 0 : null
        }
    })


    const handleSubmit = () => {
        if (!canSendForSigning.value) return
        const payload = {
            template_ids: selectedDocuments.value,
            agency_id: selectedAgency.value,
            staff_signer_user_id: selectedAgencySigner.value,
            care_for_contact_user_ids: selectedCareForClientContacts.value,
            ltc_ltci_signer_contact_user_id: selectedHipaaSigner.value ?? null,
        }

        // Only include primary and additional signers when agreement documents are selected (enabled)
        if (requiresAgreementSigners.value) {
            payload.primary_signer_contact_user_id = selectedPrimarySigner.value ?? null
            if (Array.isArray(selectedClientAgreementSigners.value) && selectedClientAgreementSigners.value.length > 0) {
                payload.additional_signer_contact_user_ids = selectedClientAgreementSigners.value
            }
        }

        // Build service_details based on selected templates and inputs
        const serviceDetails = {}

        if (hasServiceAgreementSelected.value) {
            if (serviceAgreementSelected.value?.serviceClass != null) {
                serviceDetails.service_class = serviceAgreementSelected.value.serviceClass
            }
            if (serviceAgreementSelected.value?.hourlyRate != null) {
                serviceDetails.hourly_rate = Number(serviceAgreementSelected.value.hourlyRate)
            }
            if (serviceAgreementSelected.value?.dailyRate != null) {
                serviceDetails.daily_rate = Number(serviceAgreementSelected.value.dailyRate)
            }
            if (serviceAgreementSelected.value?.nurseAgreementRate !== undefined && serviceAgreementSelected.value?.nurseAgreementRate !== null) {
                const value = serviceAgreementSelected.value.nurseAgreementRate
                // If user cleared the field (empty string), send empty string; otherwise send the numeric value
                serviceDetails.nurse_assessment_rate = value === '' ? '' : Number(value)
            }
            // Add home health care fields
            if (isHomeHealthCareSelected.value) {
                if (serviceAgreementSelected.value?.hhaFrequency != null) {
                    serviceDetails.hha_frequency = serviceAgreementSelected.value.hhaFrequency
                }
                if (serviceAgreementSelected.value?.rnAssessmentFrequency != null) {
                    serviceDetails.rn_assessment_frequency = serviceAgreementSelected.value.rnAssessmentFrequency
                }
            }
            if (serviceAgreementSelected.value?.variant != null) {
                serviceDetails.variant = serviceAgreementSelected.value.variant
            }
        }

        if (hasPlacementAgreementSelected.value) {
            if (placementAgreementSelected.value?.registrationFee != null) {
                serviceDetails.registration_fee = Number(placementAgreementSelected.value.registrationFee)
            }
            if (placementAgreementSelected.value?.permanentPlacementFee != null) {
                serviceDetails.permanent_placement_fee = Number(placementAgreementSelected.value.permanentPlacementFee)
            }
        }

        if (hasLtciAssignmentOfBenefitsSelected.value) {
            if (ltciAssignmentOfBenefitsSelected.value?.insurance_company_name != null) {
                serviceDetails.aob_insurance_company_name = ltciAssignmentOfBenefitsSelected.value.insurance_company_name
                serviceDetails.aob_insurance_policy_number = ltciAssignmentOfBenefitsSelected.value.insurance_policy_number ?? null
                serviceDetails.aob_insurance_claim_number = ltciAssignmentOfBenefitsSelected.value.insurance_claim_number ?? null
                serviceDetails.facility_agency = ltciAssignmentOfBenefitsSelected.value.facility_agency ?? null
            }
        }

        if (hasLtciAuthorizationSelected.value) {
            if (ltciAuthorizationSelected.value?.policyNumber != null) {
                serviceDetails.ltci_authorization_policy_number = ltciAuthorizationSelected.value.policyNumber
            }
            if (ltciAuthorizationSelected.value?.ssn != null) {
                serviceDetails.ltci_authorization_ssn = ltciAuthorizationSelected.value.ssn
            }
            if (ltciAuthorizationSelected.value?.insurance_company_name != null) {
                serviceDetails.ltci_authorization_insurance_company_name = ltciAuthorizationSelected.value.insurance_company_name
            }
        }

        if (hasHealthcarePatientBillOfRightsSelected.value) {
            if (healthcarePatientBillOfRightsSelected.value?.state != null) {
                serviceDetails.state = healthcarePatientBillOfRightsSelected.value.state
            }
        }

        if (hasHealthcareProxyUploadSelected.value) {
            const uploader = healthcareProxyUploadSelected.value?.uploader ?? null
            if (uploader != null) {
                serviceDetails.healthcare_proxy_uploader_contact_user_id = uploader
            }
        }

        if (hasPowerOfAttorneyUploadSelected.value) {
            const uploader = powerOfAttorneyUploadSelected.value?.uploader ?? null
            if (uploader != null) {
                serviceDetails.power_of_attorney_uploader_contact_user_id = uploader
            }
        }

        if (Object.keys(serviceDetails).length > 0) {
            payload.service_details = serviceDetails
        }

        emit('submit', payload)
    }

    const isDocumentSelected = (document, expectedId) => {
        const documentId = String(document?.id ?? '').toLowerCase()
        return documentId === String(expectedId).toLowerCase()
    }

    // Mirror incoming prop into local computed for template usage
    const isSubmitting = computed(() => props.submitting)

    // Documents that, when selected, may require healthcare proxy or power-of-attorney-upload if the hipaa signer
    // is not a client contact.
    const REQUIRED_ATTORNEY_UPLOAD_DOC_IDS = [
        'healthcare-hipaa-release',
        'ltci-authorization',
        'ltci-assignment-of-benefits',
        'healthcare-patient-bill-of-rights',
    ]
    const HEALTHCARE_PROXY_ID = 'healthcare-proxy-upload'
    const POWER_OF_ATTORNEY_ID = 'power-of-attorney-upload'

    const findCanonicalDocumentId = (key) => {
        const found = documentOptions.value.find(d => String(d.id).toLowerCase() === String(key).toLowerCase())
        return found ? found.id : key
    }

    const signerIsClientContact = (signerId) => {
        if (!signerId) return false
        const opt = clientContactOptions.value.find(o => String(o.id) === String(signerId))
        const raw = opt?.raw ?? client.value?.contacts?.find(c => String(c.id) === String(signerId))
        return raw && String(raw.type) === CARE_RECIPIENT_CONTACT_TYPE
    }

    const ensureHealthcareProxySelected = () => {
        try {
            if (!selectedHipaaSigner.value) return

            const canonicalHealthcareProxyId = findCanonicalDocumentId(HEALTHCARE_PROXY_ID)
            const canonicalPoaId = findCanonicalDocumentId(POWER_OF_ATTORNEY_ID)

            if (signerIsClientContact(selectedHipaaSigner.value)) {
                selectedDocuments.value = selectedDocuments.value.filter(id => String(id) !== String(canonicalHealthcareProxyId) && String(id) !== String(canonicalPoaId))
                return
            }

            const hasTriggerDoc = REQUIRED_ATTORNEY_UPLOAD_DOC_IDS.some(req => selectedDocumentRecords.value.some(doc => isDocumentSelected(doc, req)))
            if (!hasTriggerDoc) return

            const already = selectedDocuments.value.some(id => String(id) === String(canonicalHealthcareProxyId))
            if (!already) {
                selectedDocuments.value = [...selectedDocuments.value, canonicalHealthcareProxyId]
                healthcareProxyUploadSelected.value.uploader = selectedHipaaSigner.value
                powerOfAttorneyUploadSelected.value.uploader = selectedHipaaSigner.value
            } else {
                // ensure uploader is set to the current HIPAA signer
                healthcareProxyUploadSelected.value.uploader = selectedHipaaSigner.value
                powerOfAttorneyUploadSelected.value.uploader = selectedHipaaSigner.value
            }
        } catch (e) {
            // best-effort only
        }
    }

    // Helper: build display label for a contact id (e.g. "Alice Smith (Client)")
    function contactLabelFromId(id) {
        if (id == null) return null
        const option = clientContactOptions.value.find(o => o.id === id)
        const contact = option?.raw ?? client.value?.contacts?.find(c => c.id === id)
        if (!contact) return null
        const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim() || contact.name || 'Unknown'
        return contact.type ? `${name} (${contact.type})` : name
    }

    function formatSignerList(ids) {
        const labels = (ids ?? [])
            .map(id => contactLabelFromId(id))
            .filter(Boolean)
        if (labels.length === 0) return ''
        if (labels.length === 1) return labels[0]
        if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
        const last = labels.pop()
        return `${labels.join(', ')} and ${last}`
    }

    function buildDocumentSignerNote(document) {
        if (!document) return null
        const id = String(document.id ?? '').toLowerCase()

        const serviceDocs = ['service-agreement', 'payment-agreement', 'placement-agreement']
        const ltciDocs = ['ltci-authorization', 'ltci-assignment-of-benefits', 'healthcare-hipaa-release', 'healthcare-patient-bill-of-rights']
        const attorneyDocuments = ['healthcare-proxy-upload', 'power-of-attorney-upload']

        if (serviceDocs.includes(id)) {
            const primaryLabel = contactLabelFromId(selectedPrimarySigner.value)
            if (!primaryLabel) return null
            const additionalIds = (selectedClientAgreementSigners.value ?? [])
            const additionalList = formatSignerList(additionalIds)
            let note = `To be completed and signed by ${primaryLabel}`
            if (additionalList) note += ` and co-signed by ${additionalList}`
            return note
        }

        if (ltciDocs.includes(id)) {
            const hipaaLabel = contactLabelFromId(selectedHipaaSigner.value)
            if (!hipaaLabel) return null
            if (id === 'ltci-authorization') {
                return `To be completed and signed by ${hipaaLabel}`
            }
            return `To be signed by ${hipaaLabel}`
        }

        if (attorneyDocuments.includes(id)) {
            return 'Upload to be completed by'
        }

        return null
    }

    // Watch for contact selections and prompt for email when missing (deduplicated)
    function setupContactSelectionWatcher(targetRef) {
        let prev = []
        watch(targetRef, async (newVal, oldVal) => {
            if (skipContactSelectionWatcher.value) {
                // keep prev in-sync and do nothing while defaults are applied
                prev = Array.isArray(newVal) ? [...(newVal || [])] : (newVal == null ? null : newVal)
                return
            }

            // normalize values for array vs scalar refs
            const newNormalized = Array.isArray(newVal) ? (newVal || []) : (newVal != null ? [newVal] : [])
            const oldNormalized = Array.isArray(oldVal) ? (oldVal || []) : (oldVal != null ? [oldVal] : [])

            try {
                const added = newNormalized.filter(id => !oldNormalized.includes(id))
                if (!added || added.length === 0) {
                    prev = Array.isArray(newVal) ? [...(newVal || [])] : (newVal == null ? null : newVal)
                    return
                }

                const id = added[0]
                const option = clientContactOptions.value.find(o => o.id === id)
                const contactRaw = option?.raw ?? client.value?.contacts?.find(c => c.id === id)
                if (!contactRaw) return

                if (!clientSingleStore.hasValidEmail(contactRaw)) {
                    // remove the selection and open modal to update contact
                    if (Array.isArray(targetRef.value)) {
                        targetRef.value = targetRef.value.filter(i => i !== id)
                    } else {
                        // scalar selection (Select components): clear it
                        targetRef.value = null
                    }

                    // determine restore target label so save handler can reapply selection
                    let restoreLabel = ''
                    if (targetRef === selectedClientAgreementSigners) restoreLabel = 'signers'
                    else if (targetRef === selectedPrimarySigner) restoreLabel = 'primary'
                    else if (targetRef === selectedHipaaSigner) restoreLabel = 'hipaa'
                    else if (targetRef === healthcareProxyUploader) restoreLabel = 'uploader'
                    else if (targetRef === powerOfAttorneyUploader) restoreLabel = 'uploader'

                    contactToUpdate.value = {
                        ...JSON.parse(JSON.stringify(contactRaw)),
                        __restoreTarget: restoreLabel,
                    }
                    // close any open overlays (e.g., PrimeVue multiselect) before opening modal
                    closeOpenOverlays()
                    showEmailDialog.value = true
                }
            } finally {
                prev = Array.isArray(targetRef.value) ? [...targetRef.value] : (targetRef.value == null ? null : targetRef.value)
            }
        })
    }

    // instantiate watchers for contact selections (arrays and scalars)
    setupContactSelectionWatcher(selectedClientAgreementSigners)
    setupContactSelectionWatcher(selectedPrimarySigner)
    setupContactSelectionWatcher(selectedHipaaSigner)

    const healthcareProxyUploader = computed({
        get() {
            return healthcareProxyUploadSelected.value.uploader ?? null
        },
        set(v) {
            healthcareProxyUploadSelected.value = {
                ...healthcareProxyUploadSelected.value,
                uploader: v,
            }
        },
    })

    setupContactSelectionWatcher(healthcareProxyUploader)

    const powerOfAttorneyUploader = computed({
        get() {
            return powerOfAttorneyUploadSelected.value.uploader ?? null
        },
        set(v) {
            powerOfAttorneyUploadSelected.value = {
                ...powerOfAttorneyUploadSelected.value,
                uploader: v,
            }
        },
    })

    setupContactSelectionWatcher(powerOfAttorneyUploader)

    watch(selectedAgency, (newVal, oldVal) => {
        if (!props.show) return
        const defaultFacilityAgency = getDefaultFacilityAgency(newVal, selectedCareForContact.value?.state)
        ltciAssignmentOfBenefitsSelected.value.facility_agency = defaultFacilityAgency
    })

    // When the HIPAA signer changes, ensure POA is selected when required
    watch(selectedHipaaSigner, (newVal, oldVal) => {
        if (!props.show) return
        ensureHealthcareProxySelected()
    })

    watch(hasAttorneyUploadRequireDocSelected, (enabled) => {
        if (!props.show) return
        if (enabled) {
            ensureHealthcareProxySelected()
            return
        }

        // unselect healthcare proxy and power-of-attorney-upload if it was selected and HIPAA/Healthcare doc is no longer selected
        const canonicalHealthcareProxyId = findCanonicalDocumentId(HEALTHCARE_PROXY_ID)
        const canonicalPoaId = findCanonicalDocumentId(POWER_OF_ATTORNEY_ID)
        selectedDocuments.value = selectedDocuments.value.filter(id => String(id) !== String(canonicalHealthcareProxyId) && String(id) !== String(canonicalPoaId))
    })

    // When the user (re)selects documents, ensure mutual exclusivity based on what was added,
    // and set Healthcare Proxy uploader when Healthcare Proxy is newly selected.
    watch(selectedDocuments, (newVal, oldVal) => {
        if (!props.show) return

        const canonicalServiceId = findCanonicalDocumentId('service-agreement')
        const canonicalPaymentId = findCanonicalDocumentId('payment-agreement')
        const canonicalPlacementId = findCanonicalDocumentId('placement-agreement')

        const newArray = Array.isArray(newVal) ? [...newVal] : []
        const oldArray = Array.isArray(oldVal) ? [...oldVal] : []

        // use shared util for array equality

        // Determine which items were added in this change (new - old)
        const added = newArray.filter(id => !oldArray.some(o => String(o) === String(id)))

        if (added.length > 0) {
            const addedIncludesPlacement = added.some(id => String(id) === String(canonicalPlacementId))
            const addedIncludesServiceOrPayment = added.some(id => String(id) === String(canonicalServiceId) || String(id) === String(canonicalPaymentId))

            let filtered = newArray
            // If the user just added Placement -> remove Service and Payment
            if (addedIncludesPlacement) {
                filtered = newArray.filter(id => String(id) !== String(canonicalServiceId) && String(id) !== String(canonicalPaymentId))
            } else if (addedIncludesServiceOrPayment) {
                // If the user just added Service or Payment -> remove Placement
                filtered = newArray.filter(id => String(id) !== String(canonicalPlacementId))
            }

            if (!util.arraysEqual(filtered, selectedDocuments.value)) {
                selectedDocuments.value = filtered
                // update local newVal so POA logic below sees the updated set
                newVal = selectedDocuments.value
            }
        }

        const canonicalHealthcareProxyId = findCanonicalDocumentId(HEALTHCARE_PROXY_ID)
        const oldSelected = Array.isArray(oldVal) ? oldVal.includes(canonicalHealthcareProxyId) : false
        const newSelected = Array.isArray(newVal) ? newVal.includes(canonicalHealthcareProxyId) : false

        if (!oldSelected && newSelected) {
            healthcareProxyUploadSelected.value.uploader = selectedHipaaSigner.value
            powerOfAttorneyUploadSelected.value.uploader = selectedHipaaSigner.value
        }
    }, { deep: true })

    async function handleEmailDialogClose(contact) {
        showEmailDialog.value = false
        const restoreTarget = contactToUpdate.value?.__restoreTarget
        if (restoreTarget === 'signers') {
            selectedClientAgreementSigners.value = selectedClientAgreementSigners.value.filter(id => id !== contact?.id)
            selectKeys.signers++
        } else if (restoreTarget === 'primary') {
            selectedPrimarySigner.value = null
            selectKeys.primary++
        } else if (restoreTarget === 'hipaa') {
            selectedHipaaSigner.value = null
            selectKeys.hipaa++
        } else if (restoreTarget === 'uploader') {
            if (healthcareProxyUploadSelected.value?.uploader === contact?.id) {
                healthcareProxyUploader.value = null
            }
            if (powerOfAttorneyUploadSelected.value?.uploader === contact?.id) {
                powerOfAttorneyUploader.value = null
            }
            selectKeys.uploader++
        }
        contactToUpdate.value = null
    }

    // Close common overlay panels (PrimeVue multiselects / dropdowns) by blurring and simulating a body click
    function closeOpenOverlays() {
        try {
            if (document && document.activeElement && typeof document.activeElement.blur === 'function') {
                document.activeElement.blur()
            }
            // dispatch a click on the body to let overlay components close themselves
            document.dispatchEvent(new MouseEvent('click', { bubbles: true }))
            // fallback: hide known PrimeVue overlay elements if they're still visible
            const selectors = ['.p-multiselect-panel', '.p-multiselect-overlay', '.p-overlaypanel', '.p-overlaypanel-wrapper', '.p-dropdown-panel', '.p-overlaypane']
            selectors.forEach(sel => {
                document.querySelectorAll(sel).forEach(el => {
                    try { el.style.display = 'none' } catch (e) {}
                })
            })
        } catch (e) {
            // swallow
        }
    }

    async function handleEmailDialogSave(updatedContact) {
        if (!updatedContact || !updatedContact.id) return

        const restoreTarget = updatedContact.__restoreTarget
        delete updatedContact.__restoreTarget

        emailDialogLoading.value = true
        try {
            const response = await clientSingleStore.updateContact(client.value, updatedContact)
            // hydrate local store with updated contact
            if (response && response.data) {
                clientSingleStore.hydrateContact(response.data)
            }
            notificationStore.add('Contact updated successfully')
            showEmailDialog.value = false
            // ensure contact is selected after successful save
            // restore selection after Vue updates (hydrate/watchers) to avoid being overwritten
            skipContactSelectionWatcher.value = true
            await nextTick()
            try {
                if (restoreTarget === 'signers') {
                    if (!selectedClientAgreementSigners.value.includes(updatedContact.id)) {
                        selectedClientAgreementSigners.value.push(updatedContact.id)
                    }
                } else if (restoreTarget === 'primary') {
                    selectedPrimarySigner.value = updatedContact.id
                } else if (restoreTarget === 'hipaa') {
                    selectedHipaaSigner.value = updatedContact.id
                } else if (restoreTarget === 'uploader') {
                    // Reapply uploader selection (use computed proxy so template updates)
                    try {
                        healthcareProxyUploader.value = updatedContact.id
                    } catch (e) {
                        // fallback to direct object write
                        healthcareProxyUploadSelected.value.uploader = updatedContact.id
                    }

                    try {
                        powerOfAttorneyUploader.value = updatedContact.id
                    } catch (e) {
                        // fallback to direct object write
                        powerOfAttorneyUploadSelected.value.uploader = updatedContact.id
                    }
                }
            } finally {
                // allow watchers to resume
                nextTick(() => { skipContactSelectionWatcher.value = false })
            }
        } catch (err) {
            notificationStore.add('Failed to update contact')
        } finally {
            emailDialogLoading.value = false
        }
    }

    // Expose refs for parent component tour targeting
    defineExpose({
        getAgencySectionRef: () => agencySectionRef.value,
        getCareRecipientsSectionRef: () => careRecipientsSectionRef.value,
        getClientSignersSectionRef: () => clientSignersSectionRef.value,
        getPrimarySignerSectionRef: () => primarySignerSectionRef.value,
        getAdditionalSignersSectionRef: () => additionalSignersSectionRef.value,
        getHipaaSignerSectionRef: () => hipaaSignerSectionRef.value,
        getTemplatesSectionRef: () => templatesSectionRef.value,
    })

</script>

<template>

    <InlineModal
        :show="props.show"
        maxWidth="4xl"
        :padding-enable="false"
    >
        <div class="relative pt-5 pr-5 pl-5">
            <button
                type="button"
                class="absolute right-4 top-4 rounded-full p-1 text-stone-400 transition hover:bg-stone-100 hover:text-stone-600"
                aria-label="Close signature request dialog"
                @click="closeDialog"
            >
                <span class="text-2xl leading-none">&times;</span>
            </button>

            <div class="grid gap-5 lg:grid-cols-3">
                <section class="space-y-6 col-span-1">
                    <div ref="tour-agency-section">
                        <h3 class="mb-3 flex items-center gap-1.5 text-base font-bold text-lifeworx-blue-600">
                            <span>Agency Signer</span>
                            <button
                                type="button"
                                class="rounded-full p-0.5 transition-colors hover:bg-lifeworx-blue-100"
                                @click="emit('start-tour-at-step', 4)"
                                title="Learn more about Agency Signer"
                            >
                                <IconQuestionCircle12Filled class="size-4 text-lifeworx-blue-600" />
                            </button>
                        </h3>
                        <div class="rounded-xl border border-dashed border-lifeworx-blue-500 bg-lifeworx-blue-500 bg-opacity-5 px-4 py-4">
                            <label class="mb-5 block">
                                <Select
                                    v-model="selectedAgency"
                                    :options="agencyOptions"
                                    optionLabel="name"
                                    optionValue="id"
                                    class="w-full"
                                    checkmark
                                    :highlightOnSelect="true"
                                    :pt="{
                                        root: 'text-sm h-[34px]',
                                        label: 'text-sm',
                                        overlay: 'text-sm !z-modal',
                                    }"
                                />
                            </label>

                            <label class="block">
                                <span class="mb-1.5 block text-xs font-semibold tracking-wide text-lifeworx-blue-600">
                                    Agreements signed by
                                </span>
                                <Select
                                    v-model="selectedAgencySigner"
                                    :options="staffOptions"
                                    optionLabel="name"
                                    optionValue="id"
                                    class="w-full"
                                    checkmark
                                    :highlightOnSelect="true"
                                    :pt="{
                                        root: 'text-sm h-[34px]',
                                        label: 'text-sm',
                                        overlay: 'text-sm !z-modal',
                                    }"
                                />
                            </label>
                        </div>
                    </div>
                    <div ref="tour-care-recipients-section">
                       <h3 class="mb-3 flex items-center gap-1.5 text-base font-bold text-lifeworx-green-500">
                            <span>Care Recipients</span>
                            <button
                                type="button"
                                class="rounded-full p-0.5 transition-colors hover:bg-lifeworx-blue-100"
                                @click="emit('start-tour-at-step', 5)"
                                title="Learn more about Care Recipients"
                            >
                                <IconQuestionCircle12Filled class="size-4 text-lifeworx-blue-600" />
                            </button>
                        </h3>
                        <div>
                            <MultiSelect
                                v-model="selectedCareForClientContacts"
                                :options="clientContactOptions"
                                optionLabel="name"
                                optionValue="id"
                                display="chip"
                                :maxSelectedLabels="3"
                                :selectionLimit="2"
                                class="w-full"
                                placeholder="Select contacts"
                                :pt="{
                                    root: { class: 'text-sm' },
                                    overlay: { class: '!z-modal' },
                                    header: { class: 'hidden' },
                                }"
                            />
                        </div>
                    </div>
                    <div ref="tour-client-signers-section">
                        <h3 class="mb-3 text-base font-bold text-lifeworx-green-500">
                            Client Signers
                        </h3>
                        <div class="rounded-xl border border-dashed border-lifeworx-green-500 bg-lifeworx-green-400  bg-opacity-5 px-4 py-4 space-y-4">
                            <div ref="tour-primary-signer-section" :class="requiresAgreementSigners ? 'block' : 'block opacity-60'">
                                <span class="mb-1.5 flex items-center gap-1.5 text-xs font-semibold tracking-wide text-lifeworx-green-600">
                                    <span>Primary Signer</span>
                                    <button
                                        type="button"
                                        class="rounded-full p-0.5 transition-colors hover:bg-lifeworx-blue-100"
                                        @click="emit('start-tour-at-step', 6)"
                                        title="Learn more about Primary Signer"
                                    >
                                        <IconQuestionCircle12Filled class="size-4 text-lifeworx-blue-600" />
                                    </button>
                                </span>
                                <Select :key="selectKeys.primary"
                                    v-model="selectedPrimarySigner"
                                    :options="primaryContactOptions"
                                    optionLabel="name"
                                    optionValue="id"
                                    :disabled="!requiresAgreementSigners"
                                    class="w-full"
                                    checkmark
                                    :highlightOnSelect="true"
                                    :pt="{
                                        root: 'text-sm h-[34px]',
                                        label: 'text-sm',
                                        overlay: 'text-sm !z-modal',
                                    }"
                                />
                            </div>

                            <div ref="tour-additional-signers-section" :class="requiresAgreementSigners ? 'block' : 'block opacity-60'">
                                <span class="mb-1.5 flex items-center gap-1.5 text-xs font-semibold tracking-wide text-lifeworx-green-600">
                                    <span>Additional Signers</span>
                                    <button
                                        type="button"
                                        class="rounded-full p-0.5 transition-colors hover:bg-lifeworx-blue-100"
                                        @click="emit('start-tour-at-step', 7)"
                                        title="Learn more about Additional Signers"
                                    >
                                        <IconQuestionCircle12Filled class="size-4 text-lifeworx-blue-600" />
                                    </button>
                                </span>
                                <MultiSelect :key="selectKeys.signers"
                                    v-model="selectedClientAgreementSigners"
                                    :options="signerContactOptions"
                                    optionLabel="name"
                                    optionValue="id"
                                    :disabled="!requiresAgreementSigners"
                                    display="chip"
                                    :maxSelectedLabels="3"
                                    :selectionLimit="3"
                                    class="w-full"
                                    placeholder="Select signers"
                                    :pt="{
                                        root: { class: 'text-sm' },
                                        overlay: { class: '!z-modal' },
                                        header: { class: 'hidden' },
                                    }"
                                />
                            </div>

                            <div ref="tour-hipaa-signer-section" :class="hasAttorneyUploadRequireDocSelected ? 'block' : 'block opacity-60'">
                                <span class="mb-1.5 flex items-center gap-1.5 text-xs font-semibold tracking-wide text-lifeworx-green-600">
                                    <span>LTCI/Healthcare Auth Signer</span>
                                    <button
                                        type="button"
                                        class="rounded-full p-0.5 transition-colors hover:bg-lifeworx-blue-100"
                                        @click="emit('start-tour-at-step', 8)"
                                        title="Learn more about LTCI/Healthcare Auth Signer"
                                    >
                                        <IconQuestionCircle12Filled class="size-4 text-lifeworx-blue-600" />
                                    </button>
                                </span>
                                <Select :key="selectKeys.hipaa"
                                    v-model="selectedHipaaSigner"
                                    :options="clientContactOptions"
                                    optionLabel="name"
                                    optionValue="id"
                                    class="w-full"
                                    :disabled="!hasAttorneyUploadRequireDocSelected"
                                    checkmark
                                    :highlightOnSelect="true"
                                    :pt="{
                                        root: 'text-sm h-[34px]',
                                        label: 'text-sm',
                                        overlay: 'text-sm !z-modal',
                                    }"
                                />
                            </div>
                        </div>
                    </div>
                </section>

                <section ref="tour-templates-section" class="col-span-2">
                    <h3 class="mb-3 pl-4 text-base font-bold text-stone-400">
                        Documents & Service Details
                    </h3>
                    <div class="space-y-4 h-[560px] overflow-y-auto pr-5 pb-4 pl-4 pt-3">
                        <template v-for="(group, groupIndex) in documentGroups" :key="group.id">
                            <div class="space-y-3">
                                <div
                                    v-for="doc in group.documents"
                                    :key="doc.id"
                                    class="flex flex-col space-y-3"
                                >
                                    <div class="space-y-1">
                                        <div class="flex cursor-pointer gap-3 text-sm font-medium">
                                            <input
                                                v-model="selectedDocuments"
                                                :data-test="`document-${doc.id}`"
                                                type="checkbox"
                                                :value="doc.id"
                                                :id="doc.id"
                                                class="h-5 w-5 rounded border-stone-300 text-lifeworx-blue-700 focus:ring-lifeworx-blue-500"
                                                :disabled="doc.muted"
                                            />
                                            <label :class="['cursor-pointer', { 'font-semibold text-lifeworx-blue-700': selectedDocuments.includes(doc.id) }]" :for="doc.id">{{ doc.name }}</label>
                                        </div>
                                        <div
                                            v-if="selectedDocuments.includes(doc.id) && buildDocumentSignerNote(doc)"
                                            class="pl-8 text-stone-500 text-xs font-semibold italic"
                                            aria-live="polite"
                                        >
                                            {{ buildDocumentSignerNote(doc) }}
                                        </div>
                                    </div>
                                    <div
                                        v-if="hasServiceAgreementSelected && doc.id === 'service-agreement'"
                                        data-test="service-agreement-details"
                                        class="pl-8 pb-4"
                                    >
                                        <div class="grid gap-4 sm:grid-cols-2">
                                            <label class="block">
                                                <span class="mb-1.5 block text-xs font-semibold tracking-wide text-stone-500">
                                                    Variant
                                                </span>
                                                <Select
                                                    v-model="serviceAgreementSelected.variant"
                                                    :options="SERVICE_VARIANT_OPTIONS"
                                                    optionLabel="name"
                                                    optionValue="id"
                                                    class="w-full"
                                                    placeholder="Select variant"
                                                    :disabled="selectedAgency !== 'lifeworx-inc'"
                                                    checkmark
                                                    :highlightOnSelect="true"
                                                    :pt="{
                                                        root: 'text-sm h-[34px]',
                                                        label: 'text-sm',
                                                        overlay: 'text-sm !z-modal',
                                                    }"
                                                />
                                            </label>
                                            <label class="block">
                                                <span :class="['mb-1.5 block text-xs font-semibold tracking-wide', (hasServiceAgreementSelected && !serviceAgreementSelected.serviceClass) ? 'text-lifeworx-red-500' : 'text-stone-500']">
                                                    Service Class
                                                </span>
                                                <Select
                                                    v-model="serviceAgreementSelected.serviceClass"
                                                    :disabled="isServiceClassAutoSet"
                                                    :options="SERVICE_CLASS_OPTIONS"
                                                    optionLabel="name"
                                                    optionValue="id"
                                                    :class="['w-full', (hasServiceAgreementSelected && !serviceAgreementSelected.serviceClass) ? 'border-lifeworx-red-500 ring-1 ring-lifeworx-red-200 opacity-100' : '']"
                                                    placeholder="Select service class"
                                                    checkmark
                                                    :highlightOnSelect="true"
                                                    :pt="{
                                                        root: 'text-sm h-[34px]',
                                                        label: 'text-sm',
                                                        overlay: 'text-sm !z-modal',
                                                    }"
                                                />
                                            </label>
                                        </div>

                                        <div class="mt-4 grid gap-4 sm:grid-cols-2">
                                            <label class="block">
                                                <span :class="['mb-1.5 block text-xs font-semibold tracking-wide', (hasServiceAgreementSelected && !hasSelectedRate) ? 'text-lifeworx-red-500' : 'text-stone-500']">
                                                    Hourly Rate
                                                </span>
                                                <Select
                                                    v-model="serviceAgreementSelected.hourlyRate"
                                                    :options="hourlyRateOptions"
                                                    optionLabel="name"
                                                    optionValue="id"
                                                    show-clear
                                                    :class="['w-full', (hasServiceAgreementSelected && !hasSelectedRate) ? 'border-lifeworx-red-500 ring-1 ring-lifeworx-red-200' : '']"
                                                    placeholder="Select hourly rate"
                                                    checkmark
                                                    :highlightOnSelect="true"
                                                    :pt="{
                                                        root: 'text-sm h-[34px]',
                                                        label: 'text-sm',
                                                        overlay: 'text-sm !z-modal',
                                                    }"
                                                />
                                            </label>

                                            <label class="block">
                                                <span :class="['mb-1.5 block text-xs font-semibold tracking-wide', (hasServiceAgreementSelected && !hasSelectedRate) ? 'text-lifeworx-red-500' : 'text-stone-500']">
                                                    Daily Rate
                                                </span>
                                                <Select
                                                    v-model="serviceAgreementSelected.dailyRate"
                                                    :options="dailyRateOptions"
                                                    optionLabel="name"
                                                    optionValue="id"
                                                    show-clear
                                                    :class="['w-full', (hasServiceAgreementSelected && !hasSelectedRate) ? 'border-lifeworx-red-500 ring-1 ring-lifeworx-red-200' : '']"
                                                    placeholder="Select daily rate"
                                                    checkmark
                                                    :highlightOnSelect="true"
                                                    :pt="{
                                                        root: 'text-sm h-[34px]',
                                                        label: 'text-sm',
                                                        overlay: 'text-sm !z-modal',
                                                    }"
                                                />
                                            </label>
                                        </div>

                                        <div class="mt-4 grid gap-4 sm:grid-cols-2">
                                            <label class="block">
                                                <span class="mb-1.5 block text-xs font-semibold tracking-wide text-stone-500">
                                                    Nurse Assessment Rate
                                                </span>
                                                <TextInputWithUnit
                                                    v-model="serviceAgreementSelected.nurseAgreementRate"
                                                    type="number"
                                                    min="0"
                                                    class="mt-1 block w-full disabled:opacity-50"
                                                    unit="$"
                                                />
                                            </label>

                                            <!-- HHA Frequency and RN Assessment Frequency fields (displayed when Home Health Care) -->
                                            <template v-if="hasServiceAgreementSelected && isHomeHealthCareSelected">
                                                <div class="col-span-2">
                                                    <div class="grid gap-4 sm:grid-cols-2">
                                                        <label class="block">
                                                            <span :class="['mb-1.5 block text-xs font-semibold tracking-wide', (!serviceAgreementSelected.hhaFrequency) ? 'text-lifeworx-red-500' : 'text-stone-500']">
                                                                HHA Frequency
                                                            </span>
                                                            <TextInput
                                                                v-model="serviceAgreementSelected.hhaFrequency"
                                                                type="text"
                                                                placeholder="e.g., 7D x 12HRS"
                                                                :input_class="(!serviceAgreementSelected.hhaFrequency) ? '!border-lifeworx-red-500 ring-1 ring-lifeworx-red-200' : 'border-stone-400'"
                                                            />
                                                        </label>

                                                        <label class="block">
                                                            <span :class="['mb-1.5 block text-xs font-semibold tracking-wide', (!serviceAgreementSelected.rnAssessmentFrequency) ? 'text-lifeworx-red-500' : 'text-stone-500']">
                                                                RN Assessment Frequency
                                                            </span>
                                                            <TextInput
                                                                v-model="serviceAgreementSelected.rnAssessmentFrequency"
                                                                type="text"
                                                                placeholder="e.g., 6 months"
                                                                :input_class="(!serviceAgreementSelected.rnAssessmentFrequency) ? '!border-lifeworx-red-500 ring-1 ring-lifeworx-red-200' : 'border-stone-400'"
                                                            />
                                                        </label>
                                                    </div>
                                                    <div class="mt-3 text-stone-500 text-xs font-semibold italic">
                                                        Please confirm HHA frequency and RN Assessment Frequency with Director of Patient Services
                                                    </div>
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                    <div v-if="hasPlacementAgreementSelected && doc.id === 'placement-agreement'" data-test="placement-agreement-details" class="px-5 pb-4">
                                        <div class="grid gap-4 sm:grid-cols-2">
                                            <label class="block">
                                                <span class="mb-1.5 block text-xs font-semibold tracking-wide text-stone-500">
                                                    Registration Fee
                                                </span>
                                                <TextInputWithUnit
                                                    v-model="placementAgreementSelected.registrationFee"
                                                    type="number"
                                                    class="mt-1 block w-full disabled:opacity-50"
                                                    unit="$"
                                                />
                                            </label>
                                            <label class="block">
                                                <span class="mb-1.5 block text-xs font-semibold tracking-wide text-stone-500">
                                                    Permanent Placement Fee (%)
                                                </span>
                                                <TextInputWithUnit
                                                    v-model="placementAgreementSelected.permanentPlacementFee"
                                                    type="number"
                                                    class="mt-1 block w-full disabled:opacity-50"
                                                />
                                            </label>
                                        </div>
                                    </div>

                                    <div v-if="hasLtciAssignmentOfBenefitsSelected && doc.id === 'ltci-assignment-of-benefits'" data-test="ltci-assignment-of-benefits-details" class="pl-8 pb-4">
                                        <label class="block">
                                            <span :class="['mb-1.5 block text-xs font-semibold tracking-wide', (hasLtciAssignmentOfBenefitsSelected && !ltciAssignmentOfBenefitsSelected.insurance_company_name) ? 'text-lifeworx-red-500' : 'text-stone-500']">
                                                Insurance
                                            </span>
                                            <Select
                                                v-model="ltciAssignmentOfBenefitsSelected.insurance_company_name"
                                                :options="ltciCompanyOptions"
                                                optionLabel="name"
                                                optionValue="id"
                                                :class="['w-full', (hasLtciAssignmentOfBenefitsSelected && !ltciAssignmentOfBenefitsSelected.insurance_company_name) ? 'border-lifeworx-red-500 ring-1 ring-lifeworx-red-200' : '']"
                                                placeholder="Select company"
                                                checkmark
                                                :highlightOnSelect="true"
                                                :pt="{
                                                    root: 'text-sm h-[34px]',
                                                    label: 'text-sm',
                                                    overlay: 'text-sm !z-modal',
                                                }"
                                            />
                                        </label>
                                        <div class="mt-2 grid gap-4 sm:grid-cols-2">
                                            <label class="block">
                                                <span class="mb-1.5 block text-xs font-semibold tracking-wide text-stone-500">
                                                    Policy Number
                                                </span>
                                                <TextInput
                                                    v-model="ltciAssignmentOfBenefitsSelected.insurance_policy_number"
                                                    type="text"
                                                    class="mt-1 block w-full disabled:opacity-50"
                                                />
                                            </label>
                                            <label class="block">
                                                <span class="mb-1.5 block text-xs font-semibold tracking-wide text-stone-500">
                                                    Claim/Intake Number
                                                </span>
                                                <TextInput
                                                    v-model="ltciAssignmentOfBenefitsSelected.insurance_claim_number"
                                                    type="text"
                                                    class="mt-1 block w-full disabled:opacity-50"
                                                />
                                            </label>
                                        </div>
                                        <label class="block">
                                            <span :class="['mt-4 mb-1.5 block text-xs font-semibold tracking-wide', (hasLtciAssignmentOfBenefitsSelected && !ltciAssignmentOfBenefitsSelected.facility_agency) ? 'text-lifeworx-red-500' : 'text-stone-500']">
                                                Facility Agency
                                            </span>
                                            <Select
                                                v-model="ltciAssignmentOfBenefitsSelected.facility_agency"
                                                :options="facilityAgencyOptions"
                                                optionLabel="name"
                                                optionValue="id"
                                                :class="['w-full', (hasLtciAssignmentOfBenefitsSelected && !ltciAssignmentOfBenefitsSelected.facility_agency) ? 'border-lifeworx-red-500 ring-1 ring-lifeworx-red-200' : '']"
                                                :highlightOnSelect="true"
                                                disabled="true"
                                                :pt="{
                                                    root: 'text-sm h-[34px]',
                                                    label: 'text-sm',
                                                    overlay: 'text-sm !z-modal',
                                                }"
                                            />
                                        </label>
                                        <p class="mt-3 text-xs text-stone-500 italic">
                                            {{ careRecipientPrePopulationMessage }}
                                        </p>
                                    </div>

                                    <div v-if="hasLtciAuthorizationSelected && doc.id === 'ltci-authorization'" data-test="ltci-authorization-details" class="pl-8 pb-4">
                                        <div class="grid gap-4 sm:grid-cols-2">
                                            <label class="block">
                                                <span class="mb-1.5 block text-xs font-semibold tracking-wide text-stone-500">
                                                    Insurance
                                                </span>
                                                <Select
                                                    v-model="ltciAuthorizationSelected.insurance_company_name"
                                                    :options="ltciCompanyOptions"
                                                    optionLabel="name"
                                                    optionValue="id"
                                                    class="w-full"
                                                    placeholder="Select company"
                                                    checkmark
                                                    :highlightOnSelect="true"
                                                    :pt="{
                                                        root: 'text-sm h-[34px]',
                                                        label: 'text-sm',
                                                        overlay: 'text-sm !z-modal',
                                                    }"
                                                />
                                            </label>
                                            <label class="block">
                                                <span class="mb-1.5 block text-xs font-semibold tracking-wide text-stone-500">
                                                    SSN (Last 4 Digits)
                                                </span>
                                                <TextInput
                                                    v-model="ltciAuthorizationSelected.ssn"
                                                    type="text"
                                                    maxlength="4"
                                                    placeholder="e.g., 1234"
                                                    class="mt-1 block w-full disabled:opacity-50"
                                                />
                                            </label>
                                        </div>
                                        <label class="block">
                                            <span class="mt-4 mb-1.5 block text-xs font-semibold tracking-wide text-stone-500">
                                                Policy/Claim/Intake Number
                                            </span>
                                            <TextInput
                                                v-model="ltciAuthorizationSelected.policyNumber"
                                                type="text"
                                                placeholder="Enter claim or intake number"
                                                class="mt-1 block w-full disabled:opacity-50"
                                            />
                                        </label>
                                        <p class="mt-3 text-xs text-stone-500 italic">
                                            {{ careRecipientPrePopulationMessage }}
                                        </p>
                                    </div>

                                    <div v-if="hasHealthcarePatientBillOfRightsSelected && doc.id === 'healthcare-patient-bill-of-rights'" data-test="healthcare-patient-bill-of-rights-details" class="pl-8 pb-4">
                                        <div class="grid gap-4 sm:grid-cols-2">
                                            <label class="block">
                                                <span :class="['mb-1.5 block text-xs font-semibold tracking-wide', !healthcarePatientBillOfRightsSelected.state ? 'text-lifeworx-red-500' : 'text-stone-500']">
                                                    State
                                                </span>
                                                <Select
                                                    v-model="healthcarePatientBillOfRightsSelected.state"
                                                    :options="healthcareBillOfRightsStateOptions"
                                                    optionLabel="name"
                                                    optionValue="id"
                                                    :class="['w-full', !healthcarePatientBillOfRightsSelected.state ? 'border-lifeworx-red-500 ring-1 ring-lifeworx-red-200' : '']"
                                                    placeholder="Select state"
                                                    checkmark
                                                    :highlightOnSelect="true"
                                                    :pt="{
                                                        root: 'text-sm h-[34px]',
                                                        label: 'text-sm',
                                                        overlay: 'text-sm !z-modal',
                                                    }"
                                                />
                                            </label>
                                        </div>
                                    </div>

                                    <div v-if="hasHealthcareHipaaReleaseSelected && doc.id === 'healthcare-hipaa-release'" data-test="healthcare-hipaa-release-details" class="pl-8 pb-4">
                                        <p class="text-xs text-stone-500 italic">
                                            {{ careRecipientPrePopulationMessage }}
                                        </p>
                                    </div>

                                    <div v-if="hasHealthcareProxyUploadSelected && doc.id === 'healthcare-proxy-upload'" data-test="healthcare-proxy-upload-details" class="pl-8 pb-4">
                                        <div class="grid gap-4 sm:grid-cols-2">
                                            <label class="block">
                                                <Select :key="selectKeys.uploader"
                                                    v-model="healthcareProxyUploadSelected.uploader"
                                                    :options="clientContactOptions"
                                                    optionLabel="name"
                                                    optionValue="id"
                                                    :class="['w-full', (hasHealthcareProxyUploadSelected && !healthcareProxyUploadSelected.uploader) ? 'border-lifeworx-red-500 ring-1 ring-lifeworx-red-200' : '']"
                                                    placeholder="Select uploader"
                                                    checkmark
                                                    :highlightOnSelect="true"
                                                    :pt="{
                                                        root: 'text-sm h-[34px]',
                                                        label: 'text-sm',
                                                        overlay: 'text-sm !z-modal',
                                                    }"
                                                />
                                            </label>
                                        </div>
                                    </div>

                                    <div v-if="hasPowerOfAttorneyUploadSelected && doc.id === 'power-of-attorney-upload'" data-test="power-of-attorney-upload-details" class="pl-8 pb-4">
                                        <div class="grid gap-4 sm:grid-cols-2">
                                            <label class="block">
                                                <Select :key="selectKeys.uploader"
                                                    v-model="powerOfAttorneyUploadSelected.uploader"
                                                    :options="clientContactOptions"
                                                    optionLabel="name"
                                                    optionValue="id"
                                                    :class="['w-full', (hasPowerOfAttorneyUploadSelected && !powerOfAttorneyUploadSelected.uploader) ? 'border-lifeworx-red-500 ring-1 ring-lifeworx-red-200' : '']"
                                                    placeholder="Select uploader"
                                                    checkmark
                                                    :highlightOnSelect="true"
                                                    :pt="{
                                                        root: 'text-sm h-[34px]',
                                                        label: 'text-sm',
                                                        overlay: 'text-sm !z-modal',
                                                    }"
                                                />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div
                                v-if="groupIndex < documentGroups.length - 1"
                                data-test="document-group-divider"
                                class="ml-8 w-1/2 border-t border-stone-200"
                            />
                        </template>
                    </div>

                </section>
            </div>
        </div>
        <div class="border-t border-stone-200 py-4 px-8">
            <div class="flex flex-col gap-4 lg:flex-row lg:justify-between">
                <div
                    v-if="footerWarningText"
                    class="flex flex-grow items-center text-base font-semibold text-lifeworx-red-500"
                    data-test="signature-request-footer-warning"
                >
                    <span>{{ footerWarningText }}</span>
                </div>
                <div v-else />

                <div class="flex flex-col gap-4 sm:flex-row sm:items-center">
                    <button
                        type="button"
                        class="min-w-[160px] rounded-lg border-2 border-lifeworx-red-600 px-5 block h-10 text-base font-semibold text-lifeworx-red-600 transition hover:bg-lifeworx-red-50"
                        @click="closeDialog"
                    >
                        Cancel
                    </button>
                    <button
                        data-test="send-for-signing"
                        type="button"
                        class="min-w-[220px] rounded-lg px-5 block h-10 text-base font-semibold text-white transition"
                        :class="canSendForSigning ? 'cursor-pointer bg-lifeworx-blue-700 hover:bg-lifeworx-blue-600' : 'cursor-not-allowed bg-stone-300'"
                        :disabled="!canSendForSigning"
                        @click="handleSubmit"
                    >
                        <template v-if="isSubmitting">Sending...</template>
                        <template v-else>Send for Signing</template>
                    </button>
                </div>
            </div>
        </div>
    </InlineModal>

    <LatteUpdateContactEmailDialog
        :show="showEmailDialog"
        :contact="contactToUpdate"
        :loading="emailDialogLoading"
        @close="handleEmailDialogClose"
        @save="handleEmailDialogSave"
    />

</template>
